import { mat3 } from "gl-matrix";
import { Point } from "../models/Point";
import { Vector as Vec, Vector } from "../models/Vector";
import { SelectBoundary } from "../models/frames/SelectBoundary";
import { s } from "./globalState/settings";
import { g as gl } from "./globalState/g"
import { a, getDisplayedShapes } from "./globalState/a";
import jsPDF from "jspdf";
import { cnv } from "../libs/canvas-text/src/shared/cnv";
import { getRotateMatrixByAngleRad } from "./transform";
import { ObjectTypes } from "./enums";
import { logMeConsole } from "./logger/logger";
import { Shape } from "./types";
import { Line } from "../models/shapes/Line";
import { AbstractFrame } from "../models/frames/AbstractFrame";

function getCos(angleDeg:number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  return function () {
    return Math.cos(angleRad);
  }
}
function getSin(angleDeg:number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  return function () {
    return Math.sin(angleRad);
  }
}
function g(xOrPoint:Point | number, y:number | null = null) {
  if (y === null && typeof xOrPoint === 'number') {
    return new Point(xOrPoint as number, 0);
    }
  if (typeof xOrPoint === 'object') {
    return new Point(xOrPoint.x, xOrPoint.y);
  } else {
    return new Point(xOrPoint, y ?? 0);
  }
}
function getColor(red:number, green:number, blue:number, a:number) {
  const webglColor = [red / 255, green / 255, blue / 255, a];
  return webglColor;
}
function getAngleRadians(angleDegrees:number) {
  return angleDegrees * (Math.PI / 180);
}

function getAngleDegrees(angleRadians:number) {
  return angleRadians * (180 / Math.PI);
}
function getEccentricity(k:number) {
  const e = Math.sqrt(1 - k ** 2);
  return e;
}
function getRadius(b:number, e:number, phi:number) {
  const radius = b / (Math.sqrt(1 - (e ** 2) * (Math.cos(phi) ** 2)));
  return radius;
}
function getB(radius:number, e:number, phi:number) {
  const b = radius * Math.sqrt(1 - (e ** 2) * (Math.cos(phi) ** 2));
  return b;
}
function getMid(start:Point, end:Point) {
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  return new Point(midX, midY);
}

function transformPointByMatrix4(matrix:mat3, point:Point) {
  if (point.x === 0 && point.y === 0) {
    return g(0, 0);
  }
  const transformedPoint = Point.ORIGIN.clone();

  const x = point.x;
  const y = point.y;
  const w = 1;

  transformedPoint.x = matrix[0] * x + matrix[1] * y + matrix[3] * w + matrix[12];
  transformedPoint.y = matrix[4] * x + matrix[5] * y + matrix[7] * w + matrix[13];

  return transformedPoint;
}

function convertMatrixToPixelCoordinates(matrix:mat3, canvasWidth:number, canvasHeight:number) {
  // Calculate the scaling factors for x and y axes
  const scaleX = canvasWidth / 2;
  const scaleY = canvasHeight / 2;

  // Create a translation matrix to align with pixel coordinates
  const translationMatrix = mat3.fromValues(
    1, 0, 0,
    0, -1, 0,
    0, 0, 1
  );

  // Scale the transformation matrix to pixel coordinates
  const scaledMatrix = mat3.scale(mat3.create(), matrix, [scaleX, scaleY]);

  // Apply translation to align with pixel coordinates
  const pixelMatrix = mat3.multiply(mat3.create(), translationMatrix, scaledMatrix);

  return pixelMatrix;
}


function transformPointByMatrix3(matrix:mat3, p:Point) {
  /**
   * В этой функции матрица остаётся той же - в webgl координатах.
   * Так как на полотне теперь используются пиксели, точка преобразуется сначала
   * к координатам webgl, а затем обратно в пиксели
   */

  // TODO: здесь есть проблема с производительностью

  var canvas = gl.canvas;
  if (!canvas) {
    canvas = document.createElement("canvas");
  }

  var width = canvas.width;
  var height = canvas.height;

  if (p.x === 0 && p.y === 0) {
    return Point.ORIGIN.clone();
  }
  let transformedPoint = Point.ORIGIN.clone();
  

  const point = canvasGetWebglCoordinates(p, canvas);

  const x = point.x;
  const y = point.y;
  const w = 1;

  transformedPoint.x = matrix[0] * x + matrix[1] * y + matrix[2] * w;
  transformedPoint.y = matrix[3] * x + matrix[4] * y + matrix[5] * w;

  
  transformedPoint = convertWebGLToCanvas2DPoint(transformedPoint, width, height);

  return transformedPoint;
}
function canvasGetMouseWebgl(event:MouseEvent, canvas:HTMLCanvasElement) {
  return new Point(
    (event.clientX - canvas.offsetLeft) / canvas.width * 2 - 1,
    -(event.clientY - canvas.offsetTop) / canvas.height * 2 + 1
  )
}

function canvasGetMouse(event:MouseEvent, canvas:HTMLCanvasElement) {
  return new Point(
    (event.clientX - canvas.offsetLeft), (event.clientY - canvas.offsetTop)
  )
}

function canvasGetWebglCoordinates(position:Point, canvas:HTMLCanvasElement) {
  return new Point((position.x) / canvas.width * 2 - 1, (-position.y) / canvas.height * 2 + 1);
}

function convertPixelToWebGLCoordinate(pixelValue:number, canvasSize:number, isXAxis:boolean) {
  const canvasSizeHalf = canvasSize / 2;
  const scaleFactor = 2 / canvasSize;

  // Normalize the pixel coordinate to WebGL coordinate
  let webglCoord = (pixelValue - canvasSizeHalf) * scaleFactor;

  // Adjust for y-axis inversion in WebGL
  if (!isXAxis) {
    webglCoord *= -1;
  }

  return webglCoord;
}

function canvasGetClientY(event:MouseEvent, canvas:HTMLCanvasElement) {
  return (canvas.height - (event.clientY - canvas.offsetTop)) / canvas.height * 2 - 1;
}

function canvasGetClientX(event:MouseEvent, canvas:HTMLCanvasElement) {
  return (event.clientX - canvas.offsetLeft) / canvas.width * 2 - 1;
}

function convertVerticesToPoints(vertices:number[]) {
  let points = [];
  for (let i = 0; i < vertices.length; i += 2) {
    let point = new Point(vertices[i], vertices[i + 1]);
    points.push(point);
  }
  return points;
}

function isPointInsideFrame(frame:AbstractFrame, x:number, y:number) {
  // Check if frame and its required points exist
  if (!frame || !frame.point1 || !frame.point2 || !frame.point3) {
    return false;
  }
  
  const { point1, point2, point3 } = frame;
  if (x > point1.x && x < point2.x && ((y > point3.y && y < point2.y) || (y > point2.y && y < point3.y))) {
    return true;
  }
  return false;
}

function resizeCanvasToDisplaySize(canvas:HTMLCanvasElement) {
  // Lookup the size the browser is displaying the canvas in CSS pixels.
  const displayWidth = canvas.clientWidth;
  const displayHeight = canvas.clientHeight;

  // Check if the canvas is not the same size.
  const needResize = canvas.width !== displayWidth ||
    canvas.height !== displayHeight;

  if (needResize) {
    // Make the canvas the same size
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }

  return needResize;
}

function checkFunction(shape:Shape, functionName:string, mouse:Point) {
  return typeof (shape as any)[functionName] === 'function' ? (shape as any)[functionName](mouse) : null;
}

function convertWebGLToCanvas2DPoint(point:Point, canvas2DWidth:number, canvas2DHeight:number) {
  const canvas2DX = (point.x + 1) * canvas2DWidth / 2;
  const canvas2DY = (1 - point.y) * canvas2DHeight / 2;
  return new Point(canvas2DX, canvas2DY);

}

function convertCanvas2DToWebGLPoint(point:Point, canvas2DWidth:number, canvas2DHeight:number) {
  const webGLX = (point.x - canvas2DWidth / 2) / (canvas2DWidth / 2) - 1;
  const webGLY = (canvas2DHeight / 2 - point.y) / (canvas2DHeight / 2) - 1;
  return new Point(webGLX, webGLY);
}

function applyTransformationToPoint(x:number, y:number, matrix:mat3) {
  const newX = matrix[0] * x + matrix[3] * y + matrix[6];
  const newY = matrix[1] * x + matrix[4] * y + matrix[7];
  return new Point(newX, newY);
}

function getLineSelectBoundary(start:Point, end:Point) {
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const baseWidth = s.tolerance / 2;
  const baseOffset = 0;

  const width = baseOffset * Math.sin(angle) + baseOffset * Math.cos(angle);
  const offsetX = baseWidth * Math.sin(angle);
  const offsetY = baseWidth * Math.cos(angle);

  const selectBoundary = new SelectBoundary(Point.ORIGIN.clone(), Point.ORIGIN.clone(), Point.ORIGIN.clone(), Point.ORIGIN.clone());

  selectBoundary.p1 = new Point(start.x - offsetX + width * Math.cos(angle), start.y + offsetY + width * Math.sin(angle));
  selectBoundary.p2 = new Point(end.x - offsetX - width * Math.cos(angle), end.y + offsetY - width * Math.sin(angle));
  selectBoundary.p3 = new Point(end.x + offsetX - width * Math.cos(angle), end.y - offsetY - width * Math.sin(angle));
  selectBoundary.p4 = new Point(start.x + offsetX + width * Math.cos(angle), start.y - offsetY + width * Math.sin(angle));

  return selectBoundary
}

function getSelectBoundaryRectangle(p1:Point, p2:Point, p3:Point, p4:Point) {

  var offset = s.tolerance;
  var newP1 = new Point(p1.x - offset, p1.y - offset);
  var newP2 = new Point(p2.x + offset, p2.y - offset);
  var newP3 = new Point(p3.x + offset, p3.y + offset);
  var newP4 = new Point(p4.x - offset, p4.y + offset);

  return new SelectBoundary(newP1, newP2, newP3, newP4);
}

function getSelectBoundaryCircle(p1:Point, p2:Point, p3:Point, p4:Point) {
  const top = getLineSelectBoundary(p1, p2);
  const right = getLineSelectBoundary(p2, p3);
  const bottom = getLineSelectBoundary(p4, p3);
  const left = getLineSelectBoundary(p1, p4);

  const width = s.tolerance;

  const boundaryP1 = new Point(top.p1.x - width, top.p1.y);
  const boundaryP2 = new Point(top.p2.x + width, top.p2.y);
  const boundaryP3 = new Point(bottom.p3.x + width, bottom.p3.y);
  const boundaryP4 = new Point(bottom.p4.x - width, bottom.p4.y);

  return new SelectBoundary(boundaryP1, boundaryP2, boundaryP3, boundaryP4);
}

function isinSelectBoundaryLine(mouse:Point, start:Point, end:Point) {
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const baseWidth = s.tolerance / 2;
  const baseOffset = 0;

  const width = baseOffset * Math.sin(angle) + baseOffset * Math.cos(angle);
  const offsetX = baseWidth * Math.sin(angle);
  const offsetY = baseWidth * Math.cos(angle);

  const point1 = new Point(start.x - offsetX + width * Math.cos(angle), start.y + offsetY + width * Math.sin(angle));
  const point2 = new Point(end.x - offsetX - width * Math.cos(angle), end.y + offsetY - width * Math.sin(angle));
  const point3 = new Point(end.x + offsetX - width * Math.cos(angle), end.y - offsetY - width * Math.sin(angle));
  const point4 = new Point(start.x + offsetX + width * Math.cos(angle), start.y - offsetY + width * Math.sin(angle));

  const vertices = [
    point1.x, point1.y,
    point2.x, point2.y,
    point3.x, point3.y,
    point4.x, point4.y
  ];

  let isInside = false;
  let j = vertices.length - 2;

  for (let i = 0; i < vertices.length; i += 2) {
    const vertexX1 = vertices[i];
    const vertexY1 = vertices[i + 1];
    const vertexX2 = vertices[j];
    const vertexY2 = vertices[j + 1];

    if ((vertexY1 > mouse.y) !== (vertexY2 > mouse.y) &&
      mouse.x < ((vertexX2 - vertexX1) * (mouse.y - vertexY1)) / (vertexY2 - vertexY1) + vertexX1) {
      isInside = !isInside;
    }

    j = i;
  }
  return isInside;
}

function isHorizontal(line1:Line, line2:Line, tolerance:number) {
  var deltaY1 = Math.abs(line1.start.y - line1.end.y);
  var deltaY2 = Math.abs(line2.start.y - line2.end.y);

  if (deltaY1 < deltaY2) {
    return line1;
  }
  else {
    return line2;
  }
}

export function isLineHorizontal(segment:Line, tolerance = 0.01) {
  var dy = Math.abs(segment.start.y - segment.end.y);
  if (dy < tolerance) {
    return true;
  } else {
    return false;
  }
}



const getSideOfMouse = (mouseCoords:Point, lineCoords:Line) => {
  const dx1 = mouseCoords.x - lineCoords.start.x;
  const dy1 = mouseCoords.y - lineCoords.start.y;
  const dx2 = lineCoords.end.x - lineCoords.start.x;
  const dy2 = lineCoords.end.y - lineCoords.start.y;

  const dotProduct = dx1 * dx2 + dy1 * dy2;
  if (dotProduct > 0) {
    return 'START';
  } else if (dotProduct < 0) {
    return 'END';
  } else {
    return null;
  };
};


function getSideOfMouseRelativeToLine(mouse:Point, breakStart:Point, selectedLine:Line) {

  // Определяем координаты начала и конца выбранной линии
  const start = selectedLine.start;
  const end = selectedLine.end;

  // Определяем вектор, соединяющий начало и конец выбранной линии
  const lineVector = new Point(end.x - breakStart.x, end.y - breakStart.y);

  // Определяем вектор, соединяющий начало выбранной линии и точку мыши
  const mouseVector = new Point(mouse.x - breakStart.x, mouse.y - breakStart.y);

  // Вычисляем скалярное произведение векторов
  const scalarProduct = dot(lineVector, mouseVector);

  // Если скалярное произведение положительное, то точка мыши находится на одной стороне линии с началом
  if (scalarProduct > 0) {
    return 'end';
  }

  // Если скалярное произведение отрицательное, то точка мыши находится на одной стороне линии с концом
  if (scalarProduct < 0) {
    return 'start';
  }

  // Если скалярное произведение равно нулю, то точка мыши находится на самой линии
  return 'onLine';
}

function getProjection(mouse:Point, line:Line) {
  const lineVector = { x: line.end.x - line.start.x, y: line.end.y - line.start.y };
  const mouseVector = { x: mouse.x - line.start.x, y: mouse.y - line.start.y };

  const dotProduct = (mouseVector.x * lineVector.x + mouseVector.y * lineVector.y);
  const lineLengthSquared = lineVector.x * lineVector.x + lineVector.y * lineVector.y;

  const projectionScalar = dotProduct / lineLengthSquared;
  const projectionPoint = new Point(line.start.x + projectionScalar * lineVector.x, line.start.y + projectionScalar * lineVector.y);

  return projectionPoint;
}

function findClosestPoints(mouse:Point, points:Point[]) {
  const result: { scalar: number, point: Point }[] = points.map(p => {
    const mouseVector = new Point(mouse.x - p.x, mouse.y - p.y);
    return { scalar: getScalar(mouse, mouseVector), point: p }
  });

  let firstNegativeScalar: { scalar: number, point: Point } | null = null;
  let firstPositiveScalar: { scalar: number, point: Point } | null = null;

  result.forEach(({ scalar, point }) => {
    if (scalar < 0 && (!firstNegativeScalar || scalar > firstNegativeScalar.scalar)) {
      firstNegativeScalar = { scalar, point };
    } else if (scalar > 0 && (!firstPositiveScalar || scalar < firstPositiveScalar.scalar)) {
      firstPositiveScalar = { scalar, point };
    }
  });

  let closestPoints: Point[] = [];
  if (firstNegativeScalar && firstPositiveScalar) {
    closestPoints = [(firstNegativeScalar as { scalar: number, point: Point }).point, (firstPositiveScalar as { scalar: number, point: Point }).point];
  } else if (firstNegativeScalar) {
    closestPoints = [(firstNegativeScalar as { scalar: number, point: Point }).point];
  } else if (firstPositiveScalar) {
    closestPoints = [(firstPositiveScalar as { scalar: number, point: Point }).point];
  }

  return closestPoints;
}





const dot = (currentVector:Point, otherVector:Point) => {
  return currentVector.x * otherVector.x + currentVector.y * otherVector.y;
}

function getScalar(mouse:Point, point:Point) {

  const result = dot(mouse, point);
  return result;
}

function getDistance(mouse:Point, point:Point) {
  const result = Math.hypot(mouse.x - point.x, mouse.y - point.y)
  return result;
}
function isPointBetweenTwoPointsOnLine(point1:Point, point2:Point, selectedPoint:Point) {
  const vector1 = { x: point1.x - selectedPoint.x, y: point1.y - selectedPoint.y };
  const vector2 = { x: point2.x - selectedPoint.x, y: point2.y - selectedPoint.y };

  const dotProduct = vector1.x * vector2.x + vector1.y * vector2.y;

  if (dotProduct > 0) {
    return true;
  } else if (dotProduct < 0) {
    return false;
  } else {
    return false;
  }
}
/**
 * Функция возвращает крайнюю левую нижнюю точку. 
 * Если никакую точку нельзя отнести к крайней левой, то вычисляется
 * новая точка на основе точки с крайней координатой x и крайней координатой y
 * @param {Object} selectBoundary - область объекта. Каждая фигура имеет поле "selectBoundary", этот объект содержит 4 точки p1, p2, p3, p4.
 * 
 */
function getLowerLeftPoint(selectBoundary:SelectBoundary) {
  var { p1, p2, p3, p4 } = selectBoundary;
  var minX = Math.min(p1.x, p2.x, p3.x, p4.x);
  var maxY = Math.max(p1.y, p2.y, p3.y, p4.y);
  return g(minX, maxY);
}

function getUpperRightPoint(selectBoundary:SelectBoundary) {
  var { p1, p2, p3, p4 } = selectBoundary;
  var maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
  var minY = Math.min(p1.y, p2.y, p3.y, p4.y);
  return g(maxX, minY);
}
function getUpperLeftPoint(selectBoundary:SelectBoundary) {
  var { p1, p2, p3, p4 } = selectBoundary;
  var minX = Math.min(p1.x, p2.x, p3.x, p4.x);
  var minY = Math.max(p1.y, p2.y, p3.y, p4.y);
  return g(minX, minY);

}

function findThirdPoint(point1:Point, point2:Point, length:number) {
  let xDiff = point2.x - point1.x;
  let yDiff = point2.y - point1.y;

  let distance = Math.sqrt(xDiff * xDiff + yDiff * yDiff);

  let ratio = length / distance;

  let x3 = point1.x + ratio * xDiff;
  let y3 = point1.y + ratio * yDiff;

  return new Point(x3, y3);
}
/**
 * Функция возвращает текущий настоящий масштаб чертежа, то есть тот, который будет при печати в pdf
 */
function getRealScale() {
  const pdf = new jsPDF({
    unit: 'mm',
    format: s.format,
    orientation: "l",
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const scaleX = cnv.context?.canvas.width ? cnv.context.canvas.width / pdfWidth : 1;
  var pxScale = 1 / a.zlc;
  var realScale = pxScale * scaleX;
  return realScale;

}

function calcNewZlc(realScale:number) {

  const pdf = new jsPDF({
    unit: 'mm',
    format: s.format,
    orientation: "l",
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const scaleX = cnv.context?.canvas.width ? cnv.context.canvas.width / pdfWidth : 1;

  var pxScale = realScale / scaleX;
  var zlc = 1 / pxScale;
  return zlc;

}
function hexToNormalizedRGBA(hex:string) {
  // Remove '#' from the beginning of the hex color
  hex = hex.replace('#', '');

  // Convert hex to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  return [r, g, b, 1.0]; // Return normalized RGBA values
}

function normalizedRGBAToRGBA(normalizedRGBA:number[]) {
  // Convert normalized RGBA values to 0-255 range
  let r = Math.round(normalizedRGBA[0] * 255);
  let g = Math.round(normalizedRGBA[1] * 255);
  let b = Math.round(normalizedRGBA[2] * 255);
  let a = normalizedRGBA[3]; // Alpha value remains unchanged

  return [r, g, b]; // Return RGBA color string
}

function getTriangulatedVerticesByTwoPoints(start:Point, end:Point, width:number) {




  // Calculate the vector along the line segment
  let dx = end.x - start.x;
  let dy = end.y - start.y;




  // Calculate the normalized perpendicular vector
  let length = Math.hypot(dx, dy);
  let nx = dy / length;  // Normalized perpendicular vector x
  let ny = -dx / length; // Normalized perpendicular vector y

  let offset = -width / 2;
  let t = offset / length;
  let newStart = findPointOnRay(start, end, t);
  let newEnd = findPointOnRay(end, start, t);

  // Calculate the offset points for the rectangle corners
  let offsetX = nx * width / 2;
  let offsetY = ny * width / 2;



  // Calculate the four corners of the rectangle
  let corner1 = { x: newStart.x + offsetX, y: newStart.y + offsetY };
  let corner2 = { x: newEnd.x + offsetX, y: newEnd.y + offsetY };
  let corner3 = { x: newEnd.x - offsetX, y: newEnd.y - offsetY };
  let corner4 = { x: newStart.x - offsetX, y: newStart.y - offsetY };

  // Return the coordinates of the rectangle corners as two triangles
  return [
    corner1.x, corner1.y,
    corner2.x, corner2.y,
    corner3.x, corner3.y,
    corner1.x, corner1.y,
    corner3.x, corner3.y,
    corner4.x, corner4.y
  ];




}

/**
 * Функция находит координаты точки на луче, если известны начало и конец отрезка
 * @param {Point} start - начало отрезка
 * @param {Point} end - конец отрезка
 * @param {Number} t - Длина от точки start
 */
function findPointOnRay(start:Point, end:Point, t:number) {
  // Вычисляем координаты точки B
  let bx = start.x + (end.x - start.x) * t;
  let by = start.y + (end.y - start.y) * t;

  return new Point(bx, by);
}
/**
 * Функция определяет, находится ли точка внутри прямоугольника
 * @param {Point} p - точка, которую нужно проверить
 * @param {Point} p1 - вершина прямоугольника
 * @param {Point} p2 - вершина прямоугольника
 * @param {Point} p3 - вершина прямоугольника
 * @param {Point} p4 - вершина прямоугольника
 */
function pointInsideRectangle(p:Point, p1:Point, p2:Point, p3:Point, p4:Point) {
  // Calculate vectors from the point to each corner of the rectangle
  let vec1 = new Point(p1.x - p.x, p1.y - p.y);
  let vec2 = new Point(p2.x - p.x, p2.y - p.y);
  let vec3 = new Point(p3.x - p.x, p3.y - p.y);
  let vec4 = new Point(p4.x - p.x, p4.y - p.y);

  // Calculate the cross product of consecutive vectors
  function crossProduct(v1:Point, v2:Point) {
    return v1.x * v2.y - v1.y * v2.x;
  }

  let cross1 = crossProduct(vec1, vec2);
  let cross2 = crossProduct(vec2, vec3);
  let cross3 = crossProduct(vec3, vec4);
  let cross4 = crossProduct(vec4, vec1);

  // Check if all cross products have the same sign
  return (cross1 > 0 && cross2 > 0 && cross3 > 0 && cross4 > 0) || (cross1 < 0 && cross2 < 0 && cross3 < 0 && cross4 < 0);
}
/**
 * Функция возвращает точки пересечения прямоугольника и линии, если такие имеются.
 * @param {Point} p1 - вершина прямоугольника
 * @param {Point} p2 - вершина прямоугольника
 * @param {Point} p3 - вершина прямоугольника
 * @param {Point} p4 - вершина прямоугольника
 * @param {Point} start - начальная точка линии
 * @param {Point} end - конечная точка линии
 */
function findRectangleAndLineIntersectionPoints(p1:Point, p2:Point, p3:Point, p4:Point, start:Point, end:Point) {
  function lineIntersection(x1:number, y1:number, x2:number, y2:number, x3:number, y3:number, x4:number, y4:number) {
    let ua, ub, denom;
    denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
    if (denom === 0) {
      return null; // Lines are parallel
    }
    ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;
    if (ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1) {
      let x = x1 + ua * (x2 - x1);
      let y = y1 + ua * (y2 - y1);
      return { x, y };
    } else {
      return null; // Intersection point is outside the line segments
    }
  }

  let intersections:Point[] = [];

  // Check for intersection with each side of the rectangle
  let sides = [
    [p1, p2],
    [p2, p3],
    [p3, p4],
    [p4, p1]
  ];

  for (let side of sides) {
    let intersection = lineIntersection(side[0].x, side[0].y, side[1].x, side[1].y, start.x, start.y, end.x, end.y) as Point;
    if (intersection) {
      intersections.push(intersection);
    }
  }

  return intersections;
}

function groupPairs(arr:any[]) {
  var result = [];
  for (let i = 0; i < arr.length; i += 2) {
    if (i + 1 < arr.length) {
      result.push([arr[i], arr[i + 1]]);
    }
  }
}

/**
 * Функция определяет, пересекаются ли 2 отрезка. Используется метод псевдоскалярного умножения векторов,
 * который помогает определить, положительный угол между векторами (против часовой стрелки) или отрицательный
 * https://www.youtube.com/watch?v=OGEO4X-9uOk&t=1175s
 */
function findLinesIntersection(l1: Line, l2: Line): boolean {

  var vecAB = new Vec(l1.start, l1.end);

  var vecDA = new Vec(l2.end, l1.start);
  var vecDB = new Vec(l2.end, l1.end);
  var vecDC = new Vec(l2.end, l2.start);

  var dotDCDA = vecDC.getSkewProduct(vecDA);
  var dotDCDB = vecDC.getSkewProduct(vecDB);

  var vecAD = new Vec(l1.start, l2.end);
  var vecAC = new Vec(l1.start, l2.start);

  var dotABAC = vecAB.getSkewProduct(vecAD);
  var dotABAD = vecAB.getSkewProduct(vecAC);

  if (((dotDCDA <= 0 && dotDCDB >= 0) || (dotDCDA >= 0 && dotDCDB <= 0)) && ((dotABAC <= 0 && dotABAD >= 0) || (dotABAC >= 0 && dotABAD <= 0))) {
    return true;
  }
  return false;




}


function findIntersection(line1:Line, line2:Line) {
  // Предполагается, что классы Line и Point уже определены
  const p1 = new Point(line1.start.x, line1.start.y);
  const p2 = new Point(line1.end.x, line1.end.y);
  const v1 = p2.subtract(p1);

  const p3 = new Point(line2.start.x, line2.start.y);
  const p4 = new Point(line2.end.x, line2.end.y);
  const v2 = p4.subtract(p3);

  // Скалярное произведение векторов
  const dotProduct = v1.x * v2.x + v1.y * v2.y;

  // Длина векторов
  const length1 = Math.sqrt(v1.x ** 2 + v1.y ** 2);
  const length2 = Math.sqrt(v2.x ** 2 + v2.y ** 2);


  // Косинус угла между векторами
  const cosine = dotProduct / (length1 * length2);

  // Вывести в консоль угол между линиями в градусах


  // Если косинус близок к 1, значит линии параллельны
  if (Math.abs(cosine - 1) <= 1e-6) {
    // Векторы параллельны, значит линии пересекаются в бесконечности
    return null;
  }



  // Векторы не коллинеарны, находим точку пересечения
  const w = p1.subtract(p3);
  const x = solveSystem(v1, v2, w);

  var cross = getPointOnLine(line1.start, line1.end, x.x);



  return cross;
}

// Функция для решения системы линейных уравнений
function solveSystem(a:Point, b:Point, c:Point) {
  const det = a.x * b.y - a.y * b.x;
  return new Point((b.y * c.x - b.x * c.y) / det, (a.x * c.y - a.y * c.x) / det);
}

function getPointOnLine(start:Point, end:Point, startToM:number) {
  // Вычисляем координаты точки M
  const x = start.x - (end.x - start.x) * startToM;
  const y = start.y - (end.y - start.y) * startToM;

  return new Point(x, y);
}

/**
 * Функция проверяет, лежит ли точка на отрезке
 * @param {*} point точка, принадлежность которой проверяется
 * @param {*} segment отрезок
 * @returns boolean
 */
export function ifPointOnLine(point:Point, segment:Line) {
  var tolerance = s.tolerance;
  var start = segment.start;
  var end = segment.end;

  var { x: px, y: py } = point;
  var { x: sx, y: sy } = segment.start;
  var { x: ex, y: ey } = segment.end;

  var collinearityCheck = Math.abs((px - sx) * (ey - sy) - (py - sy) * (ex - sx)) <= tolerance;

  var withinXRange = Math.min(sx, ex) - tolerance <= px && px <= Math.max(sx, ex) + tolerance;
  var withinYRange = Math.min(sy, ey) - tolerance <= py && py <= Math.max(sy, ey) + tolerance;
  
  return collinearityCheck && withinXRange && withinYRange;
}

function getBoundingBox(shapes: Shape[]) {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  shapes.forEach(shape => {
    if (shape.type === ObjectTypes.line) {
      // Type assertion to Line
      const line = shape as Line;
      minX = Math.min(minX, line.start.x, line.end.x);
      minY = Math.min(minY, line.start.y, line.end.y);
      maxX = Math.max(maxX, line.start.x, line.end.x);
      maxY = Math.max(maxY, line.start.y, line.end.y);
    } else if (shape.type === ObjectTypes.circle) {
      // Circle: { center: Point, radius: number }
      const circle = shape as any; // Replace 'any' with your Circle type if available
      minX = Math.min(minX, circle.center.x - circle.radius);
      minY = Math.min(minY, circle.center.y - circle.radius);
      maxX = Math.max(maxX, circle.center.x + circle.radius);
      maxY = Math.max(maxY, circle.center.y + circle.radius);
    } else if (shape.type === ObjectTypes.rectangle) {
      // Rectangle: { p1: Point, width: number, height: number }
      const rect = shape as any; // Replace 'any' with your Rectangle type if available
      minX = Math.min(minX, rect.p1.x, rect.p1.x + rect.width);
      minY = Math.min(minY, rect.p1.y, rect.p1.y + rect.height);
      maxX = Math.max(maxX, rect.p1.x, rect.p1.x + rect.width);
      maxY = Math.max(maxY, rect.p1.y, rect.p1.y + rect.height);
    }
  });

  return {
    minX: minX === Number.POSITIVE_INFINITY ? 0 : minX,
    minY: minY === Number.POSITIVE_INFINITY ? 0 : minY,
    maxX: maxX === Number.NEGATIVE_INFINITY ? 0 : maxX,
    maxY: maxY === Number.NEGATIVE_INFINITY ? 0 : maxY
  };
}


/**
 * Функция определяет псевдоскалярное произведение векторов
 * между первым отрезком с открытым концом и вектором от начала этого отрезка до щелчка мыши.
 * Таким образом можно определить, с какой стороны от пути из отрезков произведён щелчок мыши.
 */
function determineSideOfOffsetRoute(route: any, mouse: any) {
  let openEnds = route.filter((s: any) => {
    return !s.startJ?.id || !s.endJ?.id;
  });

  if (openEnds.length === 0) {
    openEnds = route;
  }
  let f = openEnds[0];

  let vec = new Vector(f?.start, f?.end);
  let perpendicular = vec.getPerpendicularVector();

  let point = projectPointToVector(mouse, perpendicular);


  var mouseVec = new Vector(f?.start, point);
  var skewDot = vec.getSkewProduct(mouseVec);

  return skewDot;


}

/**
 * Функция возвращает коллекцию участков маршрута, не выбирая их
 */
function getRoute(routeId:string) {
  return getDisplayedShapes().filter((s: Shape) => (s as any).routeId === routeId);
}

function selectRouteByRoute(shapesCollection: Shape[], route: { segments: string[] }) {
  var result: Shape[] = [];
  for (const s of shapesCollection) {
    route.segments.forEach((segId: string) => {
      if (s.id === segId) {
        result.push(s);
      }
    })
  }
  return result;
}

/**
 * Функция, которая возвращает новую точку-проекцию на вектор, переданный в аргументе
 * @param {Point} point - точка, проекцию которой требуется получить
 * @param {Vector} vector - вектор, на который проецируется точка
 */
function projectPointToVector(point:Point, vector:Vector) {

  // Определяем начальную и конечную точки вектора
  const vecStart = new Point(vector.start.x, vector.start.y);
  const vecEnd = new Point(vector.end.x, vector.end.y);

  // Вычисляем направление вектора
  const vecDir = new Point(vecEnd.x - vecStart.x, vecEnd.y - vecStart.y);

  // Вектор от начала вектора до точки
  const vecToPoint = new Point(point.x - vecStart.x, point.y - vecStart.y);

  // Длина проекции вектора на направление
  const projectionDist = (vecToPoint.x * vecDir.x + vecToPoint.y * vecDir.y) /
    (vecDir.x * vecDir.x + vecDir.y * vecDir.y);

  // Координаты точки проекции
  const x = vecStart.x + projectionDist * vecDir.x;
  const y = vecStart.y + projectionDist * vecDir.y;
  const projectionPoint = new Point(x, y);

  return projectionPoint;
}

function comparePoints(point1:Point, point2:Point, tolerance:number) {
  let distance = Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2));
  return distance < tolerance;
}

function findPairs(arr: any[]) {
  let pairs: any[] = [];
  let elements: { [key: string]: boolean } = {};

  arr.forEach((element: any) => {
    const key = String(element);
    if (elements.hasOwnProperty(key)) {
      if (elements[key]) {
        pairs.push([element, element]);
        elements[key] = false;
      }
    } else {
      elements[key] = true;
    }
  });
  return pairs;
}

/**
 * Функция принимает селектор элемента в качестве аргумента и возвращает его рассчитанную высоту
 */
function getElemComputedHeight(selector:string) {
  var elem = document.querySelector(selector);
  if (!elem) return 0;
  var height = parseInt(getComputedStyle(elem).height);
  return height;
}

/**
 * Функция возвращает крайнюю верхнюю левую точку
 */
function getTopLeft(points:Point[]) {
  var mx = Number.MAX_SAFE_INTEGER;
  var my = Number.MAX_SAFE_INTEGER;

  points.forEach(p => mx = mx > p.x ? p.x : mx);
  points.forEach(p => my = my > p.y ? p.y : my);

  return new Point(mx, my);
}

/**
 * Функция возвращает крайнюю верхнюю правую точку
 */
function getTopRight(points:Point[]) {
  var mx = 0;
  var my = Number.MAX_SAFE_INTEGER;
  points.forEach(p => mx = mx < p.x ? p.x : mx);
  points.forEach(p => my = my > p.y ? p.y : my);

  return new Point(mx, my);
}

/**
 * Функция возвращает крайнюю нижнюю левую точку
 */
function getBottomLeft(points:Point[]) {
  var mx = Number.MAX_SAFE_INTEGER;
  var my = 0;
  points.forEach(p => mx = mx > p.y ? p.y : my);
  points.forEach(p => my = my < p.y ? p.y : my);

  return new Point(mx, my);
}

/**
 * Функция возвращает крайнюю нижнюю правую точку
 */
function getBottomRight(points:Point[]) {
  var mx = 0;
  var my = 0;
  points.forEach(p => mx = mx < p.x ? p.x : mx);
  points.forEach(p => my = my < p.y ? p.y : my);

  return new Point(mx, my);
}

/**
 * Функция объединяет несколько типизированных массивов
 */

function сoncatTypedArrays(arrays:any[]) {
  // sum of individual array lengths
  let totalLength = arrays.reduce((acc, value) => acc + value.length, 0);

  if (!arrays.length) return null;

  let result = new Float32Array(totalLength);

  // for each array - copy it over result
  // next array is copied right after the previous one
  let length = 0;
  for (let array of arrays) {
    result.set(array, length);
    length += array.length;
  }

  return result;
}

/**
 * Функция возвращает точку на линии
 */

function getPointOnTheLine(line:Line, firstPoint:Point, length:number) {
  // 
  let dx = line.end.x - line.start.x;
  let dy = line.end.y - line.start.y;

  let t = (length / Math.sqrt(dx * dx + dy * dy)) * (dx * (firstPoint.x - line.start.x) + dy * (firstPoint.y - line.start.y));

  return new Point(line.start.x + t * dx, line.start.y + t * dy);
}

/**
 * Функция проверяет, находится ли мышь в точке в пределах допуска
 */
function isin(point: Point, mouse: Point) {
  if (!point || !mouse) return false;

  // Scale tolerance with zoom level to maintain consistent pixel clickable area
  // a.zlc is the cumulative zoom level:
  // - zlc = 1.0 means no zoom (100%)
  // - zlc > 1.0 means zoomed in (e.g., 2.0 = 200%)
  // - zlc < 1.0 means zoomed out (e.g., 0.5 = 50%)
  // When zoomed in, visual scale is larger, so we need larger tolerance in coordinate space
  // When zoomed out, visual scale is smaller, so we need smaller tolerance in coordinate space
  const zoomLevel = a.zlc || 1.0;
  const scaledTolerance = s.tolerance * zoomLevel;

  const minX = point.x - scaledTolerance;
  const maxX = point.x + scaledTolerance;
  const minY = point.y - scaledTolerance;
  const maxY = point.y + scaledTolerance;

  var result = (mouse.x >= minX && mouse.x <= maxX && mouse.y >= minY && mouse.y <= maxY);

  return result;
}

/**
 * Функция возвращает матрицу поворота в зависимости от угла наклона отрезка
 * @param {Line} shape - фигура, ожидается класс Line
 * @param {Point} cp1 - точка соединения фигуры Free
 * @param {Point} cp2 - точка соединения фигуры Free
 * @param {Point} cpMod1 - модицированная точка соединения фигуры Free, модицикация в модуле move.js
 * @param {Point} cpMod2 - модицированная точка соединения фигуры Free, модицикация в модуле move.js
 */

export function getFreeShapeRotateMat(shape:Shape, initBlockAngleDeg:number, cpMod1:Point, cpMod2:Point) {

  if (shape.type !== ObjectTypes.line) return null;

  const line = shape as Line;
  let vec = new Vector(line.start, line.end);
  let vec0 = Vector.getVec0();

  let segAngleDeg = vec.getAngleDeg(vec0) - initBlockAngleDeg;

  let rotateMat = null;
  if (shape.isinSelectBoundary(cpMod1) && !shape.isinSelectBoundary(cpMod2)) {
    rotateMat = getRotateMatrixByAngleRad(getAngleRadians(segAngleDeg), cpMod1);
  }
  else if (shape.isinSelectBoundary(cpMod2) && !shape.isinSelectBoundary(cpMod1)) {
    rotateMat = getRotateMatrixByAngleRad(getAngleRadians(segAngleDeg), cpMod2);
  }
  return rotateMat;

}
export function getFreeShapeRotateMatByMouse(shape:Shape, initBlockAngleDeg:number, magnet:Point) {

  if (shape.type !== ObjectTypes.line) return null;

  const line = shape as Line;
  let vec = new Vector(line.start, line.end);
  let vec0 = Vector.getVec0();

  let segAngleDeg = vec.getAngleDeg(vec0) - initBlockAngleDeg;

  if (shape.isinSelectBoundary(magnet)) {
    return getRotateMatrixByAngleRad(getAngleRadians(segAngleDeg), magnet);
  }
  else {
    return null;
  }

}



/**
 * Функция проверяет, не является ли число отрицательным нулём
 */
export function isZeroNegative(zero:number) {
  const isZero = zero === 0;
  const isNegative = 1 / zero === -Infinity;
  return isNegative && isZero;
}


/**
 * Функция проверяет, является ли число положительным нулём
 */
export function isZeroPositive(zero:number) {
  const isZero = zero === 0;
  const isPositive = 1 / zero === Infinity;
  return isPositive && isZero;
}

/**
 * Функция округляет координаты точки до заданной точности
 */

export function roundPoint(point:Point) {
  var precision = 10;

  var x = parseFloat(point.x.toFixed(precision));
  var y = parseFloat(point.y.toFixed(precision));

  return new Point(x, y);
}

/*
* Функция проверяет, является ли массив "плоским" массивом
* */
export function isFlatArray(arr:any[]) {
  // Проверяем, является ли arr массивом
  if (!Array.isArray(arr)) {
    return false;
  }

  // Проходим по каждому элементу массива
  for (let item of arr) {
    // Если хотя бы один элемент сам является массивом, возвращаем false
    if (Array.isArray(item)) {
      return false;
    }
  }

  // Если ни одного элемента-массива не найдено, значит массив плоский
  return true;
}

/**
 * Функция проверяет равенство двух чисел с определённым допуском
 */
export function isAlmostEqual(x:number, etalon:number, tolerance = 1 ** 10 ^ -6) {
  // Вычисляем разницу между числами

  const difference = Math.abs(x - etalon);

  // Если разница меньше заданной точности, то числа считаются равными
  return difference <= tolerance;
}

/**
 * Функция принимает в качестве аргументов вектор и длину
 * и возвращает точку лежащую на векторе
 */
export function getPointByVectorAndLength(vector:Vector, length:number) {
  function normalizeVector(vector:Vector) {
    var length = vector.length;

    if (length === 0) {
      var start = Point.ORIGIN.clone();
      var end = Point.ORIGIN.clone();
      return new Vector(start, end);
    }

    var start = Point.ORIGIN.clone();
    var end = new Point(vector.xc / length, vector.yc / length);

    var result = new Vector(start, end);
    return result;
  }

  var normVector = normalizeVector(vector);

  
  return new Point(normVector.xc * length, normVector.yc * length);

}

/**
 * Функция принимает в качестве аргументов вектор и длину
 * и возвращает точку лежащую на векторе
 */
export function getPointByVectorAndLength2(vector:Vector, length:number) {
  function normalizeVector(vector:Vector) {
    var length = vector.length;

    if (length === 0) {
      // Zero-length vector – return a safe zero vector based on Point instances
      return new Vector(Point.ORIGIN.clone(), Point.ORIGIN.clone());
    }

    var start = Point.ORIGIN.clone();
    var end = new Point(vector.end.x / length, vector.end.y / length);

    var result = new Vector(start, end);
    return result;
  }


  var normVector = normalizeVector(vector);

  return new Point(normVector.xc * length, normVector.yc * length);
}

/**
 * Функция принимает в качестве аргументов два вектора
 * и возвращает вектор, располагающийся посредине
 */
export function getMiddleVector(startVector:Vector, endVector:Vector) {
  // Calculate the midpoint of the start points
  const midStartX = (startVector.start.x + endVector.start.x) / 2;
  const midStartY = (startVector.start.y + endVector.start.y) / 2;

  // Calculate the midpoint of the end points
  const midEndX = (startVector.end.x + endVector.end.x) / 2;
  const midEndY = (startVector.end.y + endVector.end.y) / 2;

  // Get the difference between the endpoints of the start vector
  const dx = startVector.end.x - startVector.start.x;
  const dy = startVector.end.y - startVector.start.y;

  // Translate the vector's starting point to the midpoint
  const translatedStartX = midStartX + dx / 2;
  const translatedStartY = midStartY + dy / 2;

  // Translate the vector's ending point to the midpoint
  const translatedEndX = midEndX + dx / 2;
  const translatedEndY = midEndY + dy / 2;

  // Create the final vector
  const middleVector = new Vector(
    new Point(translatedStartX, translatedStartY),
    new Point(translatedEndX, translatedEndY)
  );

  return middleVector;
}

export function getMiddlePoint(start:Point, end:Point) {
  return new Point((start.x + end.x) / 2, (start.y + end.y) / 2);
}

export function setHeights() {
  var toolbarHeight = getElemComputedHeight(".area-toolbar");
  var infoBlockHeight = getElemComputedHeight(".side-info-block");
  var appModeBlockHeight = getElemComputedHeight(".side-drawingMode-block");
  document.documentElement.style.setProperty("--toolbar-height", toolbarHeight + "px");
  document.documentElement.style.setProperty("--info-block-height", infoBlockHeight + "px");
  document.documentElement.style.setProperty("--appMode-block-height", appModeBlockHeight + "px");
}

export {
  getCos,
  getSin,
  g,
  getColor,
  getAngleRadians,
  getAngleDegrees,
  getEccentricity,
  getRadius,
  getB,
  getMid,
  transformPointByMatrix4,
  transformPointByMatrix3,
  canvasGetMouseWebgl,
  canvasGetMouse,
  canvasGetWebglCoordinates,
  convertPixelToWebGLCoordinate,
  canvasGetClientY,
  canvasGetClientX,
  convertVerticesToPoints,
  isPointInsideFrame,
  resizeCanvasToDisplaySize,
  checkFunction,
  convertWebGLToCanvas2DPoint,
  convertCanvas2DToWebGLPoint,
  applyTransformationToPoint,
  getLineSelectBoundary,
  getSelectBoundaryRectangle,
  getSelectBoundaryCircle,
  isinSelectBoundaryLine,
  isHorizontal,
  getSideOfMouse,
  getSideOfMouseRelativeToLine,
  getProjection,
  findClosestPoints,
  getScalar,
  getDistance,
  isPointBetweenTwoPointsOnLine,
  getLowerLeftPoint,
  getUpperRightPoint,
  getUpperLeftPoint,
  findThirdPoint,
  getRealScale,
  calcNewZlc as setRealScale,
  hexToNormalizedRGBA,
  normalizedRGBAToRGBA,
  getTriangulatedVerticesByTwoPoints,
  pointInsideRectangle,
  findRectangleAndLineIntersectionPoints,
  groupPairs,
  findLinesIntersection,
  findIntersection,
  getBoundingBox,
  determineSideOfOffsetRoute,

  projectPointToVector,
  comparePoints,
  selectRouteByRoute,
  findPairs,
  getRoute,
  getElemComputedHeight,

  getTopLeft,
  getTopRight,
  getBottomLeft,
  getBottomRight,

  сoncatTypedArrays,
  getPointOnLine,
  isin
};
